import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { isAxiosError } from 'axios';
import type ApiMessage from 'src/core/models/api/ApiMessage';
import type ApiMessageCategory from 'src/core/models/api/ApiMessageCategory';
import type ApiMessageDetailResponse from 'src/core/models/api/responses/ApiMessageDetailResponse';
import type ApiMessagesResponse from 'src/core/models/api/responses/ApiMessagesResponse';
import { isApiValidationErrorResponse } from 'src/core/models/api/ApiValidationErrorResponse';
import { reportOrRethrow } from 'src/core/utils/ApiCallbacks';
import Constants from 'src/core/utils/Constants';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import { reportToCallerOrCrashlytics } from 'src/domain/stores/StoreErrorChannel';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import CrashlyticsService from 'src/data/services/CrashlyticsService';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const emptyValidationErrors: Record<string, string[]> = {};
const DEFAULT_CATEGORY: number = Constants.categoryFilterAll;

const initialState = {
	loading: true,
	unreadCount: 0,
	hasIntroducedUnreadBadge: false,
	messages: [] as ApiMessage[],
	categories: [] as ApiMessageCategory[],
	selectedCategory: DEFAULT_CATEGORY,
	messageDetail: null as ApiMessageDetailResponse | null,
	messageDetailLoading: true,
	messageDetailErrorMessage: null as string | null,
	sendMessageValidationErrors: emptyValidationErrors,
};

export const useMessagesStore = create(
	combine({ ...initialState }, (set, get) => {
		const getApiMessages = async ({ update }: { update?: boolean } = {}) => {
			if (!update) {
				set({ loading: true });
			}

			await ThermenRepository.getMessages()
				.then((response) => {
					const data: ApiMessagesResponse | null = response.data;
					set({
						unreadCount: data?.unread_count ?? 0,
						messages: data?.messages ?? [],
						categories: data?.categories ?? [],
					});
				})
				.finally(() => set({ loading: false }));
		};

		const initMessageDetail = () =>
			set({ messageDetail: null, messageDetailLoading: true, messageDetailErrorMessage: null });

		const getApiMessage = async ({
			messageId,
			onSuccess,
			onError,
			onFinally,
		}: {
			messageId: string;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			if (get().messageDetail === null) {
				set({ messageDetailLoading: true });
			}
			set({ messageDetailErrorMessage: null });

			await ThermenRepository.findMessage(messageId)
				.then(async (response) => {
					const data: ApiMessageDetailResponse | null = response.data;
					set({ messageDetail: data, messageDetailLoading: false });
					await onSuccess?.();
				})
				.catch((error: ApiRequestError) => {
					set({ messageDetailErrorMessage: toErrorMessage(error), messageDetailLoading: false });
					reportToCallerOrCrashlytics({ message: 'Fetching the message failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const putApiMessageRead = ({ messageId }: { messageId: string }) => {
			const wasUnread = get().messages.some((message) => String(message.id) === messageId && !message.read);

			if (wasUnread) {
				set((state) => ({
					messages: state.messages.map((message) =>
						String(message.id) === messageId ? { ...message, read: true } : message,
					),
					unreadCount: Math.max(state.unreadCount - 1, 0),
				}));
			}

			ThermenRepository.readMessage(messageId).catch((error: ApiRequestError) => {
				if (wasUnread) {
					set((state) => ({
						messages: state.messages.map((message) =>
							String(message.id) === messageId ? { ...message, read: false } : message,
						),
						unreadCount: state.unreadCount + 1,
					}));
				}

				CrashlyticsService.captureEvent({
					message: 'Marking a message as read failed.',
					extra: { messageId, error },
				});
			});
		};

		const postApiMessage = async ({
			message,
			subject,
			workspaceId,
			reservationNumber,
			onSuccess,
			onError,
			onFinally,
		}: {
			message: string;
			subject: string;
			workspaceId: string;
			reservationNumber: string | null;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ sendMessageValidationErrors: {} });

			await ThermenRepository.sendMessage(message, subject, workspaceId, reservationNumber)
				.then(async () => await onSuccess?.())
				.catch((error: ApiRequestError) => {
					if (
						isAxiosError(error) &&
						error.response?.status === Constants.httpStatusUnprocessableEntity &&
						isApiValidationErrorResponse(error.response.data)
					) {
						set({ sendMessageValidationErrors: error.response.data.errors });
						onError?.(error);
						return;
					}

					reportOrRethrow(error, onError);
				})
				.finally(() => onFinally?.());
		};

		const findCategory = (categoryId: number) => get().categories.find((category) => category.id === categoryId);

		const filteredMessages = () => {
			const { messages, selectedCategory } = get();

			return selectedCategory === Constants.categoryFilterAll
				? messages
				: messages.filter((message) => message.category === selectedCategory);
		};

		const setSelectedCategory = (selectedCategory: number) => set({ selectedCategory });

		const clearSendMessageErrors = () => set({ sendMessageValidationErrors: {} });

		const markUnreadBadgeIntroduced = () => set({ hasIntroducedUnreadBadge: true });

		const reset = () => set({ ...initialState });

		return {
			getApiMessages,
			initMessageDetail,
			getApiMessage,
			putApiMessageRead,
			postApiMessage,
			findCategory,
			filteredMessages,
			setSelectedCategory,
			clearSendMessageErrors,
			markUnreadBadgeIntroduced,
			reset,
		};
	}),
);
